import type { ReactNode } from 'react';
import { locateTextSpans } from '@orborix/shared-types';
import { IconPen, IconWaveSmall } from '../icons.js';
import type { ConvError } from '../errorModel.js';

/**
 * Renderiza el texto del user con marcadores inline de error. Cada error matchea
 * por substring (`wrong`) y el click abre/cierra su `ErrorDetail`.
 *
 * Los errores pueden SOLAPARSE: la corrección de gramática suele cubrir una frase
 * entera que contiene varias palabras con error fonético (`think`, `thing`, …).
 * Por eso no alcanza con un cursor lineal — antes se "comía" los fonéticos
 * anidados dentro del span de gramática y sólo quedaba clickeable el primero.
 *
 * Estrategia: resolvemos cada error a un span `[start,end)`, armamos un bosque de
 * contención (frase = padre, palabra = hijo) y renderizamos recursivamente. Un
 * fonético anidado se dibuja como fragmento propio (subrayado ámbar + badge),
 * clickeable de forma independiente, mientras el resto de la frase mantiene el
 * subrayado rojo de gramática. Así conviven los dos niveles.
 *
 * `renderText` dibuja cada tramo de texto `[lo,hi)` (dentro o fuera de un
 * error): la burbuja lo usa para que las palabras se puedan tocar. Sin él, el
 * tramo va como texto plano.
 */

interface Span {
  start: number;
  end: number;
  err: ConvError;
}

interface SpanNode extends Span {
  kids: SpanNode[];
}

export function annotateText(
  text: string,
  errors: ReadonlyArray<ConvError>,
  openId: string | null,
  setOpenId: (id: string | null) => void,
  renderText: RenderText = (lo, hi) => text.slice(lo, hi),
): ReactNode {
  if (!errors || errors.length === 0) return renderText(0, text.length, false);

  // 1. Resolver cada error a una ocurrencia concreta con el matcher compartido
  //    (`locateTextSpans` de shared-types — el mismo que usa el sello del
  //    juego para saber qué chip cae en la oración juzgada). Cada error se busca
  //    de forma INDEPENDIENTE (no un cursor compartido que deje a un span ancho
  //    saltearse los angostos anidados); un `wrong` repetido se busca después
  //    del anterior, así dos errores sobre la misma palabra caen en posiciones
  //    distintas. Tolera que el corrector no haya copiado el fragmento
  //    textualmente (case / puntuación / palabra cambiada).
  const withWrong = errors.filter((err) => err.wrong);
  const spans = locateTextSpans(
    text,
    withWrong.map((err) => err.wrong),
  );
  const flat: Span[] = [];
  withWrong.forEach((err, i) => {
    const span = spans[i];
    if (span) flat.push({ start: span.start, end: span.end, err });
  });
  if (flat.length === 0) return renderText(0, text.length, false);

  // 2. Bosque de contención: el más ancho/temprano primero, luego cada span se
  //    cuelga del span abierto más cercano que lo contiene por completo.
  flat.sort((a, b) => a.start - b.start || b.end - a.end);
  const roots: SpanNode[] = [];
  const stack: SpanNode[] = [];
  for (const s of flat) {
    const node: SpanNode = { ...s, kids: [] };
    while (
      stack.length > 0 &&
      !(
        stack[stack.length - 1]!.start <= node.start &&
        stack[stack.length - 1]!.end >= node.end
      )
    ) {
      stack.pop();
    }
    const parent = stack[stack.length - 1];
    if (parent) parent.kids.push(node);
    else roots.push(node);
    stack.push(node);
  }

  const ctx: RenderCtx = { openId, setOpenId, renderText };
  return renderRange(0, text.length, roots, false, ctx);
}

/** Dibuja el tramo `[lo,hi)` del texto; `inError` = está dentro de un subrayado. */
export type RenderText = (lo: number, hi: number, inError: boolean) => ReactNode;

interface RenderCtx {
  openId: string | null;
  setOpenId: (id: string | null) => void;
  renderText: RenderText;
}

function renderRange(
  lo: number,
  hi: number,
  nodes: ReadonlyArray<SpanNode>,
  inError: boolean,
  ctx: RenderCtx,
): ReactNode[] {
  const { openId, setOpenId, renderText } = ctx;
  const out: ReactNode[] = [];
  let pos = lo;
  for (const node of nodes) {
    if (node.start > pos) {
      out.push(<span key={`t${pos}`}>{renderText(pos, node.start, inError)}</span>);
    }
    const isOpen = openId === node.err.id;
    out.push(
      <span
        key={`e${node.start}-${node.err.id}`}
        className={`err-frag err-${node.err.kind}${
          node.err.kind === 'grammar' ? ` err-sev-${node.err.severity}` : ''
        } ${isOpen ? 'is-open' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpenId(isOpen ? null : node.err.id);
        }}
      >
        {renderRange(node.start, node.end, node.kids, true, ctx)}
        <span className={`err-badge err-badge-${node.err.kind}`}>
          {node.err.kind === 'grammar' ? <IconPen size={9} /> : <IconWaveSmall size={9} />}
        </span>
      </span>,
    );
    pos = Math.max(pos, node.end);
  }
  if (pos < hi) out.push(<span key={`tend${pos}`}>{renderText(pos, hi, inError)}</span>);
  return out;
}
